import report_builder_data_selection_step_component from "@/components/probaworkflows_base_components/ReportGenerationBuilder/ReportBuilderDataSelectionStep.vue";
import {
  GET_REPORT_FORMS_URL,
  GET_REPORT_MAIN_SECTIONS_URL,
  GET_REPORT_SECTION_QUESTIONS_URL,
  GET_REPORT_SUB_SECTIONS_URL,
  get_headers,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

const build_request_config = (headers, params) => ({ headers, params });
const get_response_rows = (response) => response?.data?.data || [];
const normalize_id = (value) => String(value || "");

const build_initial_selected_questions = (question_options, selected_ids) =>
  (question_options || [])
    .filter((question_option) =>
      (selected_ids || []).includes(normalize_id(question_option.id))
    )
    .map((question_option) => ({ ...question_option }));

export default {
  name: "FormalDocumentQuestionSelector",
  components: {
    ReportBuilderDataSelectionStep: report_builder_data_selection_step_component,
  },
  mixins: [alert_mixin],
  props: {
    available_question_options: {
      type: Array,
      default: () => [],
    },
    entity_type_ids: {
      type: Array,
      default: () => [],
    },
    initial_selected_question_ids: {
      type: Array,
      default: () => [],
    },
    modal_title: {
      type: String,
      default: "Choose linked questions",
    },
    project_id: {
      type: [Number, String],
      required: true,
    },
    selection_mode: {
      type: String,
      default: "single",
    },
  },
  emits: ["close", "confirm"],
  data: () => ({
    headers: get_headers(),
    available_forms: [],
    filtered_question_rows: [],
    loading_forms: false,
    loading_main_sections: false,
    loading_questions: false,
    loading_sub_sections: false,
    main_sections: [],
    question_list: [],
    question_search: "",
    selected_form_id: "",
    selected_main_section_id: "",
    selected_questions: [],
    selected_sub_section_id: "",
    sub_sections: [],
  }),
  computed: {
    can_confirm_selection() {
      return this.selected_questions.length > 0;
    },
    empty_selected_questions_description() {
      return this.selection_mode === "multiple"
        ? "Choose one or more questions from the list on the left."
        : "Choose one question from the list on the left.";
    },
    empty_selected_questions_title() {
      return this.selection_mode === "multiple"
        ? "No questions selected yet"
        : "No question selected yet";
    },
    filtered_questions() {
      const search_term = String(this.question_search || "").trim().toLowerCase();
      if (!search_term) {
        return this.question_list;
      }
      return this.question_list.filter((question) =>
        String(question.question_text || "").toLowerCase().includes(search_term)
      );
    },
    selected_questions_description() {
      if (this.selection_mode === "multiple") {
        return "These questions will be linked into the PDF section in the order shown here.";
      }
      return "Choose the single question that should feed this PDF field.";
    },
  },
  mounted() {
    this.initialize_selector();
  },
  methods: {
    initialize_selector() {
      this.selected_questions = build_initial_selected_questions(
        this.available_question_options,
        this.initial_selected_question_ids.map(normalize_id)
      );
      this.load_forms();
    },
    translate_label(label) {
      return label;
    },
    format_question_type(question_type) {
      return String(question_type || "").split("_").join(" ");
    },
    get_allowed_metric_types() {
      return [];
    },
    is_select_type(question) {
      return Boolean(question?.is_select_type);
    },
    is_question_selected(question_id) {
      return this.selected_questions.some(
        (question) => normalize_id(question.id) === normalize_id(question_id)
      );
    },
    handle_form_selection(form_id) {
      this.selected_form_id = form_id;
      this.selected_main_section_id = "";
      this.selected_sub_section_id = "";
      this.main_sections = [];
      this.sub_sections = [];
      this.question_list = [];
      this.load_main_sections();
    },
    handle_main_section_selection(main_section_id) {
      this.selected_main_section_id = main_section_id;
      this.selected_sub_section_id = "";
      this.sub_sections = [];
      this.question_list = [];
      this.load_sub_sections();
    },
    handle_sub_section_selection(sub_section_id) {
      this.selected_sub_section_id = sub_section_id;
      this.question_list = [];
      this.load_questions();
    },
    remove_question(question_id) {
      this.selected_questions = this.selected_questions.filter(
        (question) => normalize_id(question.id) !== normalize_id(question_id)
      );
    },
    toggle_question_selection(question) {
      if (this.selection_mode === "single") {
        this.selected_questions = [question];
        return;
      }
      if (this.is_question_selected(question.id)) {
        this.remove_question(question.id);
        return;
      }
      this.selected_questions = [...this.selected_questions, question];
    },
    reorder_selected_question({ source_index, target_index }) {
      const selected_questions = [...this.selected_questions];
      const [dragged_question] = selected_questions.splice(source_index, 1);
      selected_questions.splice(target_index, 0, dragged_question);
      this.selected_questions = selected_questions;
    },
    confirm_selection() {
      this.$emit("confirm", this.selected_questions);
    },
    load_forms() {
      if (!this.entity_type_ids.length) {
        this.available_forms = [];
        return;
      }
      this.loading_forms = true;
      axios.get(
        GET_REPORT_FORMS_URL,
        build_request_config(this.headers, {
          project_id: this.project_id,
          entity_type_ids: this.entity_type_ids,
        })
      )
        .then((response) => {
          this.available_forms = get_response_rows(response);
        })
        .catch(() => {
          this.show_error("Failed to load forms for this PDF report.");
        })
        .finally(() => {
          this.loading_forms = false;
        });
    },
    load_main_sections() {
      if (!this.selected_form_id) {
        return;
      }
      this.loading_main_sections = true;
      axios.get(
        GET_REPORT_MAIN_SECTIONS_URL,
        build_request_config(this.headers, { form_id: this.selected_form_id })
      )
        .then((response) => {
          this.main_sections = get_response_rows(response);
        })
        .catch(() => {
          this.show_error("Failed to load main sections.");
        })
        .finally(() => {
          this.loading_main_sections = false;
        });
    },
    load_sub_sections() {
      if (!this.selected_main_section_id) {
        return;
      }
      this.loading_sub_sections = true;
      axios.get(
        GET_REPORT_SUB_SECTIONS_URL,
        build_request_config(this.headers, {
          form_main_section_id: this.selected_main_section_id,
        })
      )
        .then((response) => {
          this.sub_sections = get_response_rows(response);
        })
        .catch(() => {
          this.show_error("Failed to load sub-sections.");
        })
        .finally(() => {
          this.loading_sub_sections = false;
        });
    },
    load_questions() {
      if (!this.selected_sub_section_id) {
        return;
      }
      this.loading_questions = true;
      axios.get(
        GET_REPORT_SECTION_QUESTIONS_URL,
        build_request_config(this.headers, {
          form_section_id: this.selected_sub_section_id,
        })
      )
        .then((response) => {
          this.question_list = get_response_rows(response);
        })
        .catch(() => {
          this.show_error("Failed to load questions.");
        })
        .finally(() => {
          this.loading_questions = false;
        });
    },
  },
};
